/**
 * Finding, in the rendered preview, the element that shows a given field.
 *
 * ⚠️ THE RENDERER IS NOT ASKED AND IS NOT CHANGED, AND THAT IS THE WHOLE POINT. The obvious
 * implementation is `data-field="heading"` emitted by `@ksa/render`, and it was rejected twice over:
 * `SiteText` renders a bare text node with no element of its own to mark, so every one of eighteen
 * block files would have to pass a key alongside a value it already has — a second copy of the
 * registry, maintained by hand, in the package §2.7 keeps deliberately dumb. And an attribute that
 * exists only for the editor is an attribute that ships to every published page, or else a preview
 * that is not byte-for-byte what publishes (§2.2). Both are worse than searching the DOM.
 *
 * ⚠️ SO IT MATCHES ON THE RENDERED VALUE, which is the same technique the editor already uses for
 * the placeholder pictures and the empty-block labels: walk what the renderer produced and decorate
 * it. The block's props are known, the language is known, so the string on screen is known.
 *
 * ⚠️ AND IT FAILS SAFE. A field it cannot locate is simply not highlighted, and the customer opens
 * the whole form from the Edit tab as before — which is the behaviour that existed before any of
 * this, so the worst outcome is the old one.
 *
 * > ⚠️ **THIS ALSO SAID NO WRONG ELEMENT CAN EVER BE EDITED, BECAUSE AN ELEMENT IS ONLY CLAIMED BY
 * > THE FIELD WHOSE EXACT TEXT IT CONTAINS — AND THAT IS NO LONGER TRUE OF EVERY PASS.** It holds for
 * > the text pass and that pass is order-independent. It does NOT hold for the two fallbacks below,
 * > which match by document order because there is nothing to compare: an empty heading and an empty
 * > picture slot are indistinguishable from the next one. Both are marked at their definitions. The
 * > cost of being wrong there is one slot opening another's field on the same block — annoying, and
 * > nowhere near a published page.
 */

import { resolve, type Block, type BlockDef, type Lang } from '@ksa/model'

export type FieldTarget = { key: string; element: HTMLElement }

/** The kinds that have something visible to point at. Everything else lives in the full form. */
const TEXTUAL = new Set(['text', 'longText'])

/**
 * ⚠️ EXPORTED, BECAUSE `listTargets.ts` FINDS A REPEATER'S ROWS THE SAME WAY. A row is located by the
 * copy inside it, so the two files must agree on what "the element showing this string" means — and
 * two implementations of that would disagree on the first block with a nested duplicate.
 *
 * ⚠️ THE DEEPEST ELEMENT WINS. A heading's string is also the text content of the section, the
 * column and the `<div>` between them — matching the first hit would highlight half the page. The
 * one a customer means is the innermost element that contains exactly that text and nothing else.
 *
 * ⚠️ AND A CLAIMED ELEMENT IS NOT OFFERED TWICE — NOR IS ANY ANCESTOR OF ONE. Two fields holding the
 * identical string is not a corner case: a `contact` block routinely carries the same number as both
 * the phone and the WhatsApp, and a repeater's rows repeat by nature. Skipping only the claimed
 * element itself was not enough — the second field then matched the first field's PARENT, which also
 * contains exactly that text, so the mark landed back on the element already spoken for and the
 * second field was left unreachable. Refusing an ancestor of anything claimed is what actually sends
 * the search to the other copy.
 */
export function findText(root: HTMLElement, text: string, claimed: Set<Element>): HTMLElement | null {
  const wanted = text.trim()
  if (!wanted) return null

  let best: HTMLElement | null = null
  for (const element of root.querySelectorAll<HTMLElement>('*')) {
    if (claimed.has(element)) continue
    if ((element.textContent ?? '').trim() !== wanted) continue
    if (containsClaimed(element, claimed)) continue
    // Deeper than the current best, or the first hit at all.
    if (!best || best.contains(element)) best = element
  }
  return best
}

function containsClaimed(element: HTMLElement, claimed: Set<Element>): boolean {
  for (const taken of claimed) if (element.contains(taken)) return true
  return false
}

/**
 * Which elements of this block correspond to which fields.
 *
 * `root` is the block's own rendered element.
 */
export function fieldTargets(root: HTMLElement, block: Block, def: BlockDef, lang: Lang): FieldTarget[] {
  const out: FieldTarget[] = []
  const claimed = new Set<Element>()

  const textual = def.fields.filter((spec) => TEXTUAL.has(spec.type.kind))
  const unmatched: string[] = []

  for (const spec of textual) {
    const value = block.props[spec.key]
    const text = value && typeof value === 'object' ? resolve(value as never, lang) : ''
    const element = text ? findText(root, text, claimed) : null

    if (!element) {
      unmatched.push(spec.key)
      continue
    }
    claimed.add(element)
    out.push({ key: spec.key, element })
  }

  /**
   * ⚠️ AN EMPTY FIELD HAS NO TEXT TO MATCH, AND IT IS THE ONE THAT MOST NEEDS TAPPING. Every starter
   * ships with empty copy on purpose — lorem ipsum is how a builder publishes "Lorem ipsum" on a real
   * business's homepage — so on a brand-new site EVERY heading is empty and none of them would be
   * reachable by the gesture this feature is built around. `editor.css` already gives those elements
   * a `⋯` and a height for exactly this reason; this is what makes them a target as well.
   *
   * ⚠️ MATCHED BY ORDER, LIKE THE PICTURES, WITH THE SAME LIMITATION STATED OUT LOUD. The renderer
   * emits a block's text in the order the block declares it, so the nth empty slot is the nth
   * still-unmatched text field. Blocks that hide an empty field entirely — a call-to-action label
   * that is simply not rendered when blank — contribute no element and are correctly skipped, which
   * is why this pass looks at what is ON SCREEN rather than at what is missing from the props.
   */
  if (unmatched.length > 0) {
    const blanks = [...root.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6, p, li, span')].filter(
      (element) => !claimed.has(element) && (element.textContent ?? '').trim() === '',
    )
    unmatched.forEach((key, index) => {
      const element = blanks[index]
      if (!element) return
      claimed.add(element)
      out.push({ key, element })
    })
  }

  /**
   * ⚠️ PICTURES ARE MATCHED BY ORDER, NOT BY VALUE, because there is nothing to compare: an empty
   * slot renders a box with no text, no key and no attribute distinguishing it from the next one.
   * The renderer emits them in the order the block declares them, so the nth box is the nth image
   * field — and if that ever stops being true, the symptom is one picture slot opening another's
   * fields rather than anything reaching a published page.
   */
  const boxes = [...root.querySelectorAll<HTMLElement>('.s-media, .s-placeholder')]
  const imageFields = def.fields.filter((spec) => spec.type.kind === 'image')
  imageFields.forEach((spec, index) => {
    const element = boxes[index]
    if (!element || claimed.has(element)) return
    claimed.add(element)
    out.push({ key: spec.key, element })
  })

  return out
}
